import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { localizationService } from '@shared-services/localization-service';
import { Div, Span } from 'client/widget-components/basicComponents';
import { StyledButton } from 'client/widget-components/basicComponents/StyledButton';
import cssVars from 'client/widget-components/cssVariables';
import {
    mergeResponsiveStyles,
    ResponsiveStylesProp,
} from 'client/widget-components/responsive-styles/responsiveStylesService';
import React from 'react';
import styled from 'styled-components';
import { CUSTOMIZATIONS_DATA_GRABS } from '../../constants';

const { str } = localizationService;

interface ChoiceQuantityStepperProps {
    label: string;
    quantity: number;
    disableIncrement?: boolean;
    onIncrement: () => void;
    onDecrement: () => void;
    dataAuto?: string;
    /** Container styling (border, radius, width, background) — user-editable. */
    containerStyles?: ResponsiveStylesProp;
    /** Font styling, inherited from the choice label (size stays locked to 12px). */
    valueStyles?: ResponsiveStylesProp;
}

const minusIcon = (
    <svg
        width='16'
        height='16'
        viewBox='0 0 16 16'
        fill='none'
        aria-hidden='true'
    >
        <path
            d='M3.333 8h9.334'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
        />
    </svg>
);

const plusIcon = (
    <svg
        width='16'
        height='16'
        viewBox='0 0 16 16'
        fill='none'
        aria-hidden='true'
    >
        <path
            d='M8 3.333v9.334M3.333 8h9.334'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
        />
    </svg>
);

const ChoiceQuantityStepper = ({
    label,
    quantity,
    disableIncrement,
    onIncrement,
    onDecrement,
    dataAuto,
    containerStyles,
    valueStyles,
}: ChoiceQuantityStepperProps) => {
    const stepperLabel = str(
        'ui.runtimessr.productCustomizations.quantity.stepperAriaLabel',
        { label }
    );
    const decrementLabel = str(
        'ui.runtimessr.productCustomizations.quantity.decrementAriaLabel',
        { label }
    );
    const incrementLabel = str(
        'ui.runtimessr.productCustomizations.quantity.incrementAriaLabel',
        { label }
    );

    return (
        <Div
            role='group'
            aria-label={stepperLabel}
            data-auto={dataAuto}
            data-grab={CUSTOMIZATIONS_DATA_GRABS.choiceQuantityStepperDataGrab}
            styles={mergeResponsiveStyles([
                defaultStepperStyles,
                containerStyles,
            ])}
        >
            <IconButton
                aria-label={decrementLabel}
                title={decrementLabel}
                data-grab={
                    CUSTOMIZATIONS_DATA_GRABS.choiceQuantityButtonDataGrab
                }
                data-auto={dataAuto && `${dataAuto}-decrease`}
                styles={iconStyles}
                onClick={onDecrement}
            >
                {minusIcon}
            </IconButton>
            <Span
                aria-live='polite'
                aria-atomic='true'
                data-grab={
                    CUSTOMIZATIONS_DATA_GRABS.choiceQuantityValueDataGrab
                }
                data-auto={dataAuto && `${dataAuto}-value`}
                styles={mergeResponsiveStyles([
                    defaultValueStyles,
                    valueStyles,
                    lockedValueStyles,
                ])}
            >
                {quantity}
            </Span>
            <IconButton
                aria-label={incrementLabel}
                title={incrementLabel}
                data-grab={
                    CUSTOMIZATIONS_DATA_GRABS.choiceQuantityButtonDataGrab
                }
                data-auto={dataAuto && `${dataAuto}-increase`}
                disabled={disableIncrement}
                styles={mergeResponsiveStyles([
                    iconStyles,
                    ...(disableIncrement ? [disabledIconStyles] : []),
                ])}
                onClick={onIncrement}
            >
                {plusIcon}
            </IconButton>
        </Div>
    );
};

export default ChoiceQuantityStepper;

const defaultStepperStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        minWidth: '79px',
        height: '22px',
        padding: '0 4px',
        overflow: 'hidden',
        background: 'transparent',
        border: `1px solid ${cssVars.borderColor}`,
        borderRadius: '36px',
        color: 'inherit',
    },
};

const defaultValueStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        lineHeight: '20px',
        letterSpacing: '0.2px',
        textAlign: 'center',
        color: 'inherit',
        cursor: 'default',
    },
};

// Font size is locked to 12px regardless of the choice-label styling.
const lockedValueStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        fontSize: '12px',
    },
    [RuntimeMediaQuery.DESKTOP]: {
        fontSize: '12px',
    },
    [RuntimeMediaQuery.MOBILE]: {
        fontSize: '12px',
    },
    [RuntimeMediaQuery.TABLET]: {
        fontSize: '12px',
    },
    [RuntimeMediaQuery.MOBILE_IMPLICIT]: {
        fontSize: '12px',
    },
    [RuntimeMediaQuery.TABLET_IMPLICIT]: {
        fontSize: '12px',
    },
};

const iconStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: '16px',
        height: '16px',
        color: 'inherit',
    },
};

const disabledIconStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        cursor: 'not-allowed',
        opacity: 0.35,
    },
};

// Base button (reset + type="button") extended with the hover/focus states the
// flat responsive-styles system can't express.
const IconButton = styled(StyledButton)`
    &:hover:not(:disabled) {
        opacity: 0.7;
    }

    &:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 1px;
        border-radius: 50%;
    }
`;
